import { groupBy } from '@/services/sanitize.service';
import db from '@/rxdb/database';
import { combineLatest, firstValueFrom, map, mergeMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { getCachedObservable } from '@/rxdb/observablesCache';

function mapAmendment(item) {
    if (item) {
        const plainItem = item.toMutableJSON();
        return {
            ...plainItem,
            emissionDueDate: plainItem.emissionDueDate ? new Date(plainItem.emissionDueDate) : null,
            emissionDate: plainItem.emissionDate ? new Date(plainItem.emissionDate) : null,
        };
    } else {
        return null;
    }
}

export async function bulkInsert(projectId, amendments) {
    return db.getProjectCollections(projectId).amendments.bulkInsert(amendments);
}
function mapAmendments(dbAmendments) {
    const amendments = dbAmendments.map(mapAmendment);
    return groupBy(amendments, 'groupId').map((versions) => {
        versions.reverse();
        return {
            ...versions[0],
            versions,
        };
    });
}
function mergeToAmendment(amendmentId, dbAmendments) {
    const versions = dbAmendments.map(mapAmendment).reverse();
    const amendment = versions.find((amendment) => amendment.id === amendmentId);
    return {
        ...amendment,
        versions,
    };
}

export function getAmendments(projectId) {
    return getCachedObservable('getAmendments_' + projectId, () =>
        db
            .getProjectCollections(projectId)
            .amendments.find({ sort: [{ createdAt: 'asc' }] })
            .$.pipe(map((amendments) => mapAmendments(amendments))),
    );
}

export function getAmendment(projectId, amendmentId) {
    return db
        .getProjectCollections(projectId)
        .amendments.findOne({ selector: { id: amendmentId } })
        .$.pipe(
            mergeMap((amendment) =>
                amendment
                    ? combineLatest([
                          firstValueFrom(
                              db.getProjectCollections(projectId).amendments.find({
                                  selector: { groupId: amendment.groupId },
                                  sort: [{ createdAt: 'asc' }],
                              }).$,
                          ),
                      ])
                    : null,
            ),
        )
        .pipe(map(([amendments]) => mergeToAmendment(amendmentId, amendments)));
}

export function removeAmendment(projectId, id) {
    return db
        .getProjectCollections(projectId)
        .amendments.findOne({ selector: { id: id } })
        .remove();
}

export async function createAmendment(projectId, item) {
    const result = await db.getProjectCollections(projectId).amendments.insert({
        projectId,
        locationIds: [],
        groupId: uuidv4(),
        description: '',
        ...item,
        emissionDueDate: item.emissionDueDate ? item.emissionDueDate.toISOString() : null,
        emissionDate: item.emissionDate ? item.emissionDate.toISOString() : null,
    });
    return result.toJSON();
}

export async function updateAmendment(projectId, amendment) {
    const dbAmendment = await db
        .getProjectCollections(projectId)
        .amendments.findOne({ selector: { id: amendment.id } })
        .exec();
    const patch = { ...JSON.parse(JSON.stringify(amendment)) };
    if (amendment.emissionDate === null || !!amendment.emissionDate) {
        patch.emissionDate = amendment.emissionDate ? amendment.emissionDate.toISOString() : null;
    }
    if (amendment.emissionDueDate === null || !!amendment.emissionDueDate) {
        patch.emissionDueDate = amendment.emissionDueDate ? amendment.emissionDueDate.toISOString() : null;
    }
    delete patch.conclusion;
    return dbAmendment.atomicPatch(patch);
}

export async function removeAmendmentGroup(projectId, amendmentGroupId) {
    const entities = await db
        .getProjectCollections(projectId)
        .amendments.find({ selector: { groupId: amendmentGroupId } })
        .exec();
    return Promise.all(entities.map((entity) => entity.remove()));
}
